import React, { RefObject } from "react";
import {
  Box,
  Typography,
  Paper,
  Avatar,
  Chip,
  Divider,
  CircularProgress,
  useTheme as useMuiTheme,
} from "@mui/material";
import { formatDateSeparator, formatRelativeTime, isSameDay } from "../helpers/dateUtils";
import { capitalizeFirstLetter } from "../helpers/stringUtils";
import { useTheme } from "../contexts/ThemeContext";
import { APP_NAME } from "../helpers/constants";
import { getLoggedInUserData } from "../helpers/storageUtils";

/**
 * JWT-backed auth state often omits `name`; login stores full profile in LOGGED_IN_USER_DATA (`name`, `email_id`).
 * Merge so optimistic bubbles (Ask Prism7, etc.) show the real display name at runtime.
 */
function getCurrentUserLabelParts(
  user: { name?: string; full_name?: string; email?: string } | null | undefined
): { displayName: string; email: string } {
  const stored = getLoggedInUserData();
  const displayName =
    user?.full_name?.trim() ||
    user?.name?.trim() ||
    (typeof stored?.full_name === "string" ? stored.full_name.trim() : "") ||
    (typeof stored?.name === "string" ? stored.name.trim() : "") ||
    "";
  const email =
    user?.email?.trim() ||
    (stored && typeof stored.email_id === "string" ? stored.email_id.trim() : "") ||
    "";
  return { displayName, email };
}

/** Minimal message shape required for list logic (sort/dedupe and date separators) */
export interface ThreadMessageMinimal {
  id: string;
  created_at: string;
  sequence_number?: number;
  /** Optional: used as tie-breaker so user message appears before AI response when created_at is equal */
  message_type?: "user" | "system" | "email";
}

export interface ThreadMessageListProps<T extends ThreadMessageMinimal> {
  /** Messages to display (parent may pass filtered list for panel) */
  messages: T[];
  /** Render a single message (bubble, avatar, content, etc.) */
  renderMessage: (message: T) => React.ReactNode;
  /** Optional: optimistic user message while post is in flight (same chrome as a real user bubble, no "Posting..." chip) */
  temporaryUserMessage?: string | null;
  /** Optional: show AI "Thinking" placeholder */
  isAiThinking?: boolean;
  /** Optional: show AI "Generating draft" placeholder */
  isGeneratingDraft?: boolean;
  /** Current user for temporary message avatar/name (prefer full_name over login name; no email-prefix fallback) */
  user?: { name?: string; full_name?: string; email?: string } | null;
  /** Ref for scroll-into-view anchor at end of list */
  messagesEndRef?: RefObject<HTMLDivElement | null>;
}

function ThreadMessageListInner<T extends ThreadMessageMinimal>({
  messages,
  renderMessage,
  temporaryUserMessage,
  isAiThinking,
  isGeneratingDraft,
  user,
  messagesEndRef,
}: ThreadMessageListProps<T>) {
  const muiTheme = useMuiTheme();
  const { isDarkMode } = useTheme();

  // Deduplicate by id only; then sort by created_at ascending so oldest is top, latest is bottom
  const uniqueMessages = messages.reduce((acc, message) => {
    const existing = acc.find((m) => m.id === message.id);
    if (!existing) acc.push(message);
    return acc;
  }, [] as T[]);
  // Oldest first (top), latest last (bottom); when same time, user message before AI (system) response
  const displayMessages = [...uniqueMessages].sort((a, b) => {
    const t1 = new Date(a.created_at).getTime();
    const t2 = new Date(b.created_at).getTime();
    if (t1 !== t2) return t1 - t2;
    const typeOrder = (m: T) => (m.message_type === "user" ? 0 : m.message_type === "system" ? 1 : 2);
    return typeOrder(a) - typeOrder(b);
  });

  return (
    <Box data-testid="messages-list" sx={{ mb: 0, minWidth: 0, wordBreak: "break-word", overflowWrap: "break-word" }}>
      {displayMessages.map((message, index) => {
        const showDateSeparator =
          index === 0 ||
          !isSameDay(
            message.created_at,
            displayMessages[index - 1].created_at
          );
        // Stable key by id so prepending does not change existing keys
        return (
          <React.Fragment key={message.id}>
            {showDateSeparator && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginTop: "12px",
                  marginBottom: "12px",
                  position: "relative",
                }}
              >
                <Divider
                  sx={{
                    position: "absolute",
                    left: 0,
                    right: 0,
                    width: "100%",
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    position: "relative",
                    backgroundColor: isDarkMode
                      ? muiTheme.palette.background.default
                      : muiTheme.palette.background.paper,
                    px: 2,
                    color: "text.secondary",
                    fontSize: "0.875rem",
                  }}
                >
                  {formatDateSeparator(message.created_at)}
                </Typography>
              </Box>
            )}
            {renderMessage(message)}
          </React.Fragment>
        );
      })}

      {/* Temporary user message: matches real user bubbles (name, User Message tag, time) — no "Posting..." chip */}
      {temporaryUserMessage &&
        (() => {
          const { displayName, email } = getCurrentUserLabelParts(user);
          const initial = (displayName.charAt(0) || email.charAt(0) || "U").toUpperCase();
          const title = capitalizeFirstLetter(displayName) || "User";
          return (
        <Box
          sx={{
            mb: 2,
            p: 2,
            borderRadius: 3,
            backgroundColor: isDarkMode
              ? muiTheme.palette.grey[700]
              : muiTheme.palette.grey[50],
            border: `1px solid ${muiTheme.palette.divider}`,
            position: "relative",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 1,
              mb: 1,
            }}
          >
            <Avatar
              sx={{
                width: 32,
                height: 32,
                mt: 0.5,
                backgroundColor: "#4caf50",
                fontSize: "0.875rem",
                fontWeight: "bold",
              }}
            >
              {initial}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 0.5 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                <Typography variant="body2" sx={{ fontWeight: "bold", color: "text.primary" }}>
                  {title}
                </Typography>
                <Chip
                  label="User Message"
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: "0.75rem",
                    backgroundColor: "#e8f5e8",
                    color: "#2e7d32",
                    border: "none",
                  }}
                />
              </Box>
              <Typography variant="caption" color="text.secondary">
                {formatRelativeTime(new Date().toISOString())}
              </Typography>
            </Box>
          </Box>
          <Typography
            variant="body2"
            sx={{
              whiteSpace: "pre-wrap",
              lineHeight: 1.6,
              color: "text.primary",
              fontSize: "0.875rem",
              fontFamily: "inherit",
            }}
          >
            {temporaryUserMessage}
          </Typography>
        </Box>
          );
        })()}

      {/* AI Thinking / Generating draft placeholder */}
      {(isAiThinking || isGeneratingDraft) && (
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 2,
            borderRadius: 2,
            // grey[50] is always light; use dark surface in dark mode (same idea as user placeholder bubble)
            backgroundColor: isDarkMode
              ? muiTheme.palette.grey[800]
              : muiTheme.palette.grey[50],
            border: `1px solid ${muiTheme.palette.divider}`,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
            <Avatar
              sx={{
                width: 32,
                height: 32,
                backgroundColor: "#1976d2",
                fontSize: "0.875rem",
                fontWeight: "bold",
              }}
            >
              S
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: "bold", color: "text.primary" }}
                >
                  {APP_NAME || "Prism 7"}
                </Typography>
                <Chip
                  label="Thinking"
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: "0.75rem",
                    backgroundColor: isDarkMode
                      ? muiTheme.palette.grey[600]
                      : "#fff3cd",
                    color: isDarkMode
                      ? muiTheme.palette.warning.light
                      : "#856404",
                  }}
                />
              </Box>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <CircularProgress size={16} sx={{ mr: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  Processing your request...
                </Typography>
              </Box>
            </Box>
          </Box>
        </Paper>
      )}

      {/* Auto-scroll anchor at end of list */}
      {messagesEndRef && (
        <div ref={messagesEndRef} style={{ height: "1px" }} />
      )}
    </Box>
  );
}

/** Thread message list with date separators and optional placeholders. Reusable in main content and Ask Prism7 panel. */
const ThreadMessageList = ThreadMessageListInner as <T extends ThreadMessageMinimal>(
  props: ThreadMessageListProps<T>
) => React.ReactElement;

export default ThreadMessageList;
